import { Link } from 'react-router-dom';
import { Folder, FolderOpen, ChevronRight, AlertCircle, CheckCircle2, Clock, FileX } from 'lucide-react';

const STATE_STYLES = {
  issues: {
    label: '(Issues)',
    cls: 'text-[var(--color-danger-700)] font-bold',
    icon: <AlertCircle className="w-4 h-4 text-[var(--color-danger-600)]" />,
    folderTone: 'text-[var(--color-danger-600)]',
  },
  okay: {
    label: '(OKAY)',
    cls: 'text-[var(--color-recovery-700)] font-bold',
    icon: <CheckCircle2 className="w-4 h-4 text-[var(--color-recovery-600)]" />,
    folderTone: 'text-[var(--color-recovery-600)]',
  },
  awaiting_review: {
    label: '(Needs Review)',
    cls: 'text-[var(--color-action-700)] font-bold',
    icon: <Clock className="w-4 h-4 text-[var(--color-action-600)]" />,
    folderTone: 'text-[var(--color-action-600)]',
  },
  not_scanned: {
    label: '(Scans Missing)',
    cls: 'text-[var(--color-danger-700)] font-bold',
    icon: <FileX className="w-4 h-4 text-[var(--color-danger-600)]" />,
    folderTone: 'text-[var(--color-warm-400)]',
  },
};

function rollupState(pathways) {
  // Date-level rollup: red if any pathway has issues OR scans missing,
  // amber if any pathway awaiting review, green if all clean,
  // grey if none scanned at all.
  const states = Object.values(pathways || {})
    .map(p => p.status)
    .filter(s => s && s !== 'not_implemented');
  if (states.length === 0) return 'not_scanned';
  if (states.includes('issues') || states.includes('not_scanned')) {
    return states.includes('not_scanned') && !states.includes('issues')
      ? 'not_scanned' : 'issues';
  }
  if (states.includes('awaiting_review')) return 'awaiting_review';
  return 'okay';
}

export default function StatusFolderRow({ dateEntry, basePath }) {
  const state = rollupState(dateEntry.pathways);
  const style = STATE_STYLES[state];
  const cod = dateEntry.pathways?.cod_checks || {};
  const dateObj = new Date(dateEntry.date + 'T00:00:00');
  const day = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Link
      to={`${basePath}/${dateEntry.date}`}
      className="group flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)] hover:shadow-sm transition-all"
    >
      <Folder className={`w-5 h-5 ${style.folderTone} group-hover:hidden shrink-0`} />
      <FolderOpen className={`w-5 h-5 ${style.folderTone} hidden group-hover:block shrink-0`} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-[var(--color-warm-900)] text-[14px]">{dateStr}</span>
          <span className="text-[12px] text-[var(--color-warm-500)]">{day}</span>
          <span className={`text-[13px] ${style.cls} flex items-center gap-1`}>
            {style.icon}
            {style.label}
          </span>
        </div>
        {state !== 'not_scanned' && (
          <div className="mt-1 text-[11px] text-[var(--color-warm-500)] flex items-center gap-3">
            {cod.needs_action > 0 && <span><span className="font-semibold text-[var(--color-danger-700)]">{cod.needs_action}</span> need action</span>}
            {cod.awaiting_review > 0 && <span><span className="font-semibold text-[var(--color-action-700)]">{cod.awaiting_review}</span> awaiting review</span>}
            {cod.passed > 0 && <span><span className="font-semibold text-[var(--color-recovery-700)]">{cod.passed}</span> passed</span>}
            {cod.total === 0 && state === 'okay' && <span>Nothing audited</span>}
          </div>
        )}
      </div>

      <ChevronRight className="w-4 h-4 text-[var(--color-warm-400)] group-hover:text-[var(--color-warm-600)] shrink-0" />
    </Link>
  );
}
